import React, { useState } from "react";
import { Box, Tooltip, TooltipPopup, TooltipTrigger } from "@salestrak/ui";

/**
 * Property-label tooltip shared by every card action (Age, Comments,
 * Assignees — reference parity): hover names the property; on a coarse
 * pointer a held press does (the library tooltip's press-reveal), yielding to
 * a clipped value inside the action, which is the innermost trigger. The
 * label may carry the property's figure ("3 unread", "2 comments") — the one
 * hover surface a card action gets; no action opens a preview popover. The
 * trigger is a wrapping Box, so any child — a Button, a selector, a popover
 * subtree — composes without prop forwarding.
 */
export function CardActionTooltip({
  label,
  children,
  fill,
  disabled,
}: {
  label: string;
  children: React.ReactNode;
  /** Stretch the trigger wrapper to fill its row so a truncating child (the
   *  address chip) is bounded to the card width instead of sizing to content
   *  and overflowing. */
  fill?: boolean;
  /** Stand down while a press-opened overlay on the same trigger is up (the
   *  ghost's Updates popover) — the label would float beside the very surface
   *  it announced. Needed because a press INSIDE the child button stops
   *  propagation before the tooltip trigger sees it. */
  disabled?: boolean;
}) {
  // Always controlled so `disabled` can hold it shut without flipping the
  // tooltip between controlled and uncontrolled; the hover delay still runs
  // and just asks for state through onOpenChange.
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open && !disabled} onOpenChange={setOpen}>
      <TooltipTrigger
        render={
          <Box
            direction="row"
            minWidth={0}
            shrink
            fill={fill ? "width" : undefined}
          >
            {children}
          </Box>
        }
      />
      {/* Left side (reference parity): property tooltips hang off the card's edge
          instead of covering the rows above. */}
      <TooltipPopup side="left">{label}</TooltipPopup>
    </Tooltip>
  );
}
